import { ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/shared/lib/cn';

export interface NodoMenu {
  id: string;
  etiqueta: string;
  /** Hoja del arbol: navega a esta ruta. */
  ruta?: string;
  hijos?: readonly NodoMenu[];
  /** Marca la pantalla como pendiente de desarrollo. */
  enDesarrollo?: boolean;
}

export interface PropsMenuArbol {
  nodos: readonly NodoMenu[];
  rutaActiva: string;
  onNavegar: (ruta: string) => void;
  /** Ids de las carpetas abiertas al montar. */
  abiertosPorDefecto?: readonly string[];
}

/**
 * Menu lateral en arbol, con la misma organizacion en carpetas que el sistema actual:
 * SISTEMA, CONFIGURAR, GENERALES, ADMINISTRACION, STOCK, VENTAS, COMPRAS,
 * INFORMES Y PRESENTACIONES, ACOPIO y TRANSPORTE.
 *
 * El arbol se arma desde una configuracion, no desde el layout, para que sumar un
 * modulo nuevo no implique tocar la estructura de la pantalla.
 */
export function MenuArbol({
  nodos,
  rutaActiva,
  onNavegar,
  abiertosPorDefecto = [],
}: PropsMenuArbol) {
  const [abiertos, setAbiertos] = useState<Set<string>>(() => new Set(abiertosPorDefecto));

  function alternar(id: string): void {
    setAbiertos((actuales) => {
      const siguiente = new Set(actuales);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });
  }

  return (
    <nav aria-label="Menu principal" className="py-1 text-sm">
      <ul role="tree" className="space-y-0.5">
        {nodos.map((nodo) => (
          <NodoDelArbol
            key={nodo.id}
            nodo={nodo}
            nivel={0}
            abiertos={abiertos}
            onAlternar={alternar}
            rutaActiva={rutaActiva}
            onNavegar={onNavegar}
          />
        ))}
      </ul>
    </nav>
  );
}

interface PropsNodo {
  nodo: NodoMenu;
  nivel: number;
  abiertos: Set<string>;
  onAlternar: (id: string) => void;
  rutaActiva: string;
  onNavegar: (ruta: string) => void;
}

function NodoDelArbol({ nodo, nivel, abiertos, onAlternar, rutaActiva, onNavegar }: PropsNodo) {
  const tieneHijos = Boolean(nodo.hijos && nodo.hijos.length > 0);
  const abierto = abiertos.has(nodo.id);
  const activo = Boolean(nodo.ruta) && nodo.ruta === rutaActiva;
  const sangria = { paddingLeft: `${0.5 + nivel * 0.85}rem` };

  if (!tieneHijos) {
    return (
      <li role="none">
        <button
          type="button"
          role="treeitem"
          aria-selected={activo}
          aria-current={activo ? 'page' : undefined}
          style={sangria}
          onClick={() => nodo.ruta && onNavegar(nodo.ruta)}
          className={cn(
            'flex w-full items-center gap-1.5 rounded-sm py-1 pr-2 text-left',
            activo
              ? 'bg-marca-600 font-semibold text-white'
              : 'text-marca-50 hover:bg-marca-700/70 hover:text-white',
          )}
        >
          <span className="flex-1 truncate">{nodo.etiqueta}</span>
          {nodo.enDesarrollo ? (
            <span
              className="rounded-sm bg-amber-400/90 px-1 text-xs font-semibold text-amber-950"
              title="Pantalla en desarrollo"
            >
              EN DES.
            </span>
          ) : null}
        </button>
      </li>
    );
  }

  return (
    <li role="none">
      <button
        type="button"
        role="treeitem"
        aria-expanded={abierto}
        style={sangria}
        onClick={() => onAlternar(nodo.id)}
        className="flex w-full items-center gap-1 rounded-sm py-1 pr-2 text-left font-semibold tracking-wide text-white hover:bg-marca-700/70"
      >
        {abierto ? (
          <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-3.5 shrink-0" aria-hidden="true" />
        )}
        {abierto ? (
          <FolderOpen className="size-3.5 shrink-0 text-marca-200" aria-hidden="true" />
        ) : (
          <Folder className="size-3.5 shrink-0 text-marca-200" aria-hidden="true" />
        )}
        {/* Los nombres de carpeta son largos ("INFORMES Y PRESENTACIONES"): se parten
            en dos renglones en vez de cortarse con puntos suspensivos. */}
        <span className="leading-tight">{nodo.etiqueta}</span>
      </button>

      {abierto ? (
        <ul role="group" className="mt-0.5 space-y-0.5">
          {nodo.hijos!.map((hijo) => (
            <NodoDelArbol
              key={hijo.id}
              nodo={hijo}
              nivel={nivel + 1}
              abiertos={abiertos}
              onAlternar={onAlternar}
              rutaActiva={rutaActiva}
              onNavegar={onNavegar}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
